import { Button } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyText } from '@/lib/clipboard';

export interface CopyButtonProps {
  text: string;
  /** Defaults to "Copy". */
  label?: string;
  size?: 'xs' | 'sm';
  variant?: 'default' | 'subtle';
  className?: string;
  /** Accessible name when the visible label repeats per row; must contain the visible label. */
  ariaLabel?: string;
}

type CopyState = 'idle' | 'copied' | 'failed';

export function CopyButton({
  text,
  label,
  size = 'xs',
  variant = 'default',
  className,
  ariaLabel,
}: CopyButtonProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<CopyState>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  const copy = async () => {
    setState((await copyText(text)) ? 'copied' : 'failed');
  };

  return (
    <Button
      size={size}
      variant={variant}
      className={className}
      onClick={() => void copy()}
      aria-live="polite"
      aria-label={state === 'idle' ? ariaLabel : undefined}
    >
      {state === 'copied'
        ? t('ui.copied')
        : state === 'failed'
          ? t('ui.copyFailed')
          : (label ?? t('ui.copy'))}
    </Button>
  );
}
